import { Form, InputNumber, Modal, notification, Typography } from 'antd'
import { useForm, useWatch } from 'antd/es/form/Form'
import { FC } from 'react'
import styled from 'styled-components'
import formatNumber from '../../../../../helpers/formatters/format-any-number'
import useEndpointQueryParams from '../../../../../hooks/table-pages/use-endpoint-query-parameters'
import { defaultParams } from '../../../constants/default-params'
import { deserializeFilters } from '../../../helpers/deserialize-filters'
import { splitLogisticsCost } from '../../../helpers/split-logistics-cost'
import { useSetLogisticsCost } from '../../../hooks/use-set-logistics-cost'
import { DataSourceItem } from '../../../shipments-page'

const SharesList = styled.ul`
    max-height: 240px;
    overflow-y: auto;
    margin: 0;
    padding-left: 20px;
`

interface FormValues {
    total?: number
}

interface SetLogisticsCostModalProps {
    open: boolean
    onClose: () => void
    selectedRows: DataSourceItem[]
}

const SetLogisticsCostModal: FC<SetLogisticsCostModalProps> = ({ open, onClose, selectedRows }) => {
    const [form] = useForm<FormValues>()
    const params = useEndpointQueryParams({ deserializeFilters })
    const queryArg = { ...params, ...defaultParams }
    const total = useWatch('total', form)
    const { setLogisticsCost, isLoading } = useSetLogisticsCost(queryArg)
    const shares = total != null ? splitLogisticsCost(total, selectedRows.length) : []

    const onOk = async () => {
        try {
            const values = await form.validateFields()
            const failed = await setLogisticsCost(selectedRows, values.total ?? 0)

            if (failed.length) {
                notification.error({
                    message: 'Не удалось сохранить логистику',
                    description: `Отгрузки: ${failed.map(s => s.id).join(', ')}`,
                })
                return
            }

            notification.success({ message: 'Стоимость логистики сохранена' })
            form.resetFields()
            onClose()
        } catch {
            // ошибки валидации показывает форма
        }
    }

    return (
        <Modal
            title={'Указать стоимость логистики'}
            open={open}
            okText={'Сохранить'}
            onOk={onOk}
            onCancel={onClose}
            okButtonProps={{ loading: isLoading }}
            destroyOnClose
        >
            <Typography.Paragraph>
                Сумма делится поровну между выбранными отгрузками ({selectedRows.length} шт.) и
                записывается в колонку «Логистика». Прежние значения логистики в этих отгрузках
                будут заменены, себестоимость и прибыль пересчитаются.
            </Typography.Paragraph>
            <Form form={form} layout={'vertical'}>
                <Form.Item
                    name={'total'}
                    label={'Общая сумма, руб.'}
                    rules={[{ required: true, message: 'Укажите сумму' }]}
                >
                    <InputNumber autoFocus min={0} controls={false} />
                </Form.Item>
            </Form>
            {shares.length > 0 && (
                <>
                    <Typography.Text type={'secondary'}>Будет записано:</Typography.Text>
                    <SharesList>
                        {selectedRows.map((shipment, index) => (
                            <li key={shipment.documentId}>
                                Отгрузка {shipment.id} — {formatNumber(shares[index])} руб.
                            </li>
                        ))}
                    </SharesList>
                </>
            )}
        </Modal>
    )
}

export default SetLogisticsCostModal
